/**
 * SundayAutoMode — the "one-click" mode.
 *
 * Fast path to a portfolio for anyone who doesn't want to hand-tune. The
 * full WarPaint rule engine (Combat Stack Matrix, Anti-Chalk Governor,
 * Native Ownership Model, Monte Carlo variance, Vegas Lock Protocol,
 * Fingerprint Variance, Cluster Rotation) still runs in the background.
 *
 * Two-Touch flow:
 *   • If the user has already made ≥2 substantive touches (locks / excludes /
 *     boosts / exposure caps / proj overrides), we skip the gate and generate
 *     immediately.
 *   • Otherwise we open the Two-Touch Gate — a modal that requires the user
 *     to pick a value sleeper (Touch 1) and a ceiling anchor + slider
 *     position (Touch 2). Those choices are written as real player_overrides
 *     so every user's portfolio is genuinely differentiated.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Lightning, CheckCircle, WarningCircle, GearSix, Rocket, ShieldCheck, Prohibit, Sliders, Ghost,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFieldCoverage, FieldCoverageBadge, CoverageConfirmDialog, needsCoverageConfirm } from "@/components/FieldCoverage";
import PreflightPanel from "@/components/PreflightPanel";
import { countSubstantiveTouches } from "@/lib/compliance";
import StrategyPicker from "@/components/StrategyPicker";
import TwoTouchGate from "@/components/TwoTouchGate";
import ConfidenceCopy from "@/components/ConfidenceCopy";
import FlexShareRules, { FLEX_CAPS_DEFAULT, normalizeFlexCaps, flexCapsIssue, flexCapsPayload } from "@/components/FlexShareRules";

// Sensible default lineup counts per preset. Used to seed the numLineups
// input when the user picks a new contest — they can still override it.
const DEFAULT_LINEUPS = {
  milly_maker: 150, "20_max": 20, three_max: 3, single_entry_150: 1,
  small_gpp: 20, cash_50_50: 3, head_to_head: 3, flea_flicker: 150,
  big_play_25: 20,
};


// Mirrors backend contest_sizing.SHOWDOWN_ANCHORS: nearest anchor in log-space picks the ruleset name.
const SD_ANCHORS = [[150, "small"], [1500, "mid"], [10000, "large"], [50000, "milly"]];
export const showdownTierOf = (fieldSize) => {
  const fs = Math.max(1, Number(fieldSize) || 0);
  let key = fs <= SD_ANCHORS[0][0] ? "small" : fs >= SD_ANCHORS[3][0] ? "milly" : null;
  let blend = null;
  if (!key) {
    for (let i = 0; i < SD_ANCHORS.length - 1; i++) {
      const [lf, lk] = SD_ANCHORS[i]; const [hf, hk] = SD_ANCHORS[i + 1];
      if (fs >= lf && fs <= hf) {
        const t = (Math.log(fs) - Math.log(lf)) / (Math.log(hf) - Math.log(lf));
        key = t >= 0.5 ? hk : lk;
        blend = { lo: lk, hi: hk, t };
        break;
      }
    }
  }
  const meta = {
    milly: { name: "SHOWDOWN MILLY MAKER", rules: "CPT ≤20% · 35% leverage captains · 4-script mix · dup cap · Field-EV ranking" },
    large: { name: "SHOWDOWN LARGE GPP", rules: "CPT ≤30% · 25% leverage captains · 3-script mix" },
    mid: { name: "SHOWDOWN MID GPP", rules: "CPT ≤40% · 15% leverage captains" },
    small: { name: "SHOWDOWN SMALL FIELD", rules: "chalk captain allowed (≤60%) · game-script build" },
  }[key || "small"];
  const blendNote = blend && blend.lo !== blend.hi ? ` · dials ${Math.round(blend.t * 100)}% of the way from ${blend.lo} to ${blend.hi}` : "";
  return { key, ...meta, blendNote };
};

export default function SundayAutoMode({ onGenerated, userOverrides = {}, onOverridesChange, slateType = null }) {
  const [preset, setPreset] = useState(
    (typeof localStorage !== "undefined" && localStorage.getItem("wp_active_preset")) || "milly_maker"
  );
  const [presetMap, setPresetMap] = useState({});
  const [numLineups, setNumLineups] = useState(150);
  // Always-fresh mirror so a build started from any closure (gate proceed,
  // deferred run) posts exactly the count on screen — never a stale 150.
  const numLineupsRef = useRef(150);
  numLineupsRef.current = numLineups;
  // Sprint 77 — Crown Spread slider: user-chosen max captain share (Showdown only).
  const cptShareRef = useRef((() => {
    try { const v = Number(localStorage.getItem("wp_cpt_max_share")); return Number.isFinite(v) && v > 0 ? v : null; } catch { return null; }
  })());
  const runRef = useRef(null);
  useEffect(() => {
    const onRebuild = (e) => {
      const v = Number(e?.detail?.cpt_max_share_pct);
      cptShareRef.current = Number.isFinite(v) && v > 0 ? v : null;
      try {
        if (cptShareRef.current) localStorage.setItem("wp_cpt_max_share", String(cptShareRef.current));
        else localStorage.removeItem("wp_cpt_max_share");
      } catch { /* noop */ }
      runRef.current?.();
    };
    window.addEventListener("wp:rebuild-showdown", onRebuild);
    return () => window.removeEventListener("wp:rebuild-showdown", onRebuild);
  }, []);
  // Sprint 60.68 — new simplified contest picker. When contestType is
  // set the backend derives all rule numbers dynamically via
  // `derive_config(contest_type, field_size, max_entries)`. Users
  // never see per-rule numbers. Legacy preset picker below still
  // works for advanced flows (Split Portfolio, etc).
  const [contestType, setContestType] = useState("MILLY");   // MILLY | GPP | CASH | SHOWDOWN
  // Sprint 64.2 — Cash is a family, not a ruleset. The sub-type (payout
  // curve) is a required second pick and is what the backend routes on.
  const CASH_SUBTYPES = [
    { key: "DOUBLE_UP", label: "Double-Up", hint: "Top ~44% doubles", field: 1000, entries: 1 },
    { key: "FIFTY_FIFTY", label: "50-50", hint: "Top 50% ≈1.8×", field: 1000, entries: 1 },
    { key: "HEAD_TO_HEAD", label: "Head-to-Head", hint: "1v1 · max chalk", field: 2, entries: 1 },
  ];
  const [cashSubType, setCashSubType] = useState(null);
  const [fieldSize, setFieldSize] = useState(200000);
  const [maxEntries, setMaxEntries] = useState(150);
  // Sprint 60.104 — reset field size + max entries to each preset's
  // baked-in defaults on contest-type switch. Previously the stale
  // value from the prior contest type would linger (e.g., click GPP
  // → set field to 35000 → click Milly Maker → still shows 35000
  // "locked" even though Milly's baked-in field is 200k). Users read
  // the stale 35k as if the optimizer would target that field.
  const _CONTEST_DEFAULTS = {
    MILLY: { field: 200000, entries: 150 },
    GPP: { field: 35000, entries: 20 },
    CASH: { field: 100, entries: 1 },
    SHOWDOWN: { field: 20000, entries: 20 },
  };
  const applyContestType = (ct) => {
    setContestType(ct);
    if (ct !== "CASH") setCashSubType(null);
    const d = _CONTEST_DEFAULTS[ct];
    if (d) { setFieldSize(d.field); setMaxEntries(d.entries); }
    if (!userEditedLineups && d) setNumLineups(d.entries);
    setEntryFee(_DEFAULT_FEE[ct] ?? 20);
  };
  const applyCashSubType = (key) => {
    setCashSubType(key);
    const s = CASH_SUBTYPES.find((x) => x.key === key);
    if (s) { setFieldSize(s.field); setMaxEntries(s.entries); if (!userEditedLineups) setNumLineups(s.entries); }
  };
  const cashSubTypeMissing = contestType === "CASH" && !cashSubType;
  // Sprint 64.6 — pull the contest shape straight from the DK lobby.
  const [dkContestRef, setDkContestRef] = useState("");
  const [dkContestId, setDkContestId] = useState(null);
  const [dkPulling, setDkPulling] = useState(false);
  const pullDkContest = async () => {
    const m = String(dkContestRef).match(/(\d{6,})/);
    if (!m) { toast.error("Paste a DraftKings contest ID or contest URL."); return; }
    setDkPulling(true);
    try {
      const c = await api.dkContest(m[1]);
      setDkContestId(c.contest_id);
      const fam = ["DOUBLE_UP", "FIFTY_FIFTY", "HEAD_TO_HEAD"].includes(c.contest_type_guess) ? "CASH" : c.contest_type_guess;
      setContestType(fam);
      setCashSubType(fam === "CASH" ? c.contest_type_guess : null);
      if (c.field_size > 0) setFieldSize(c.field_size);
      if (c.max_entries_per_user > 0) { setMaxEntries(c.max_entries_per_user); if (!userEditedLineups) setNumLineups(c.max_entries_per_user); }
      if (c.entry_fee > 0) setEntryFee(c.entry_fee);
      toast.success(`${c.name} · field ${Number(c.field_size).toLocaleString()} · ${c.max_entries_per_user}-max · $${c.entry_fee}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that contest from the DK lobby.");
    } finally { setDkPulling(false); }
  };
  // Sprint 64.3 — Showdown is decided by the SLATE (1 game), not the tab.
  // A 1-game slate auto-selects Showdown; the Showdown tab on a multi-game
  // slate is blocked with a clear pointer to the slate picker.
  const slateIsShowdown = slateType === "SHOWDOWN";
  const showdownMismatch = contestType === "SHOWDOWN" && slateType && !slateIsShowdown;
  useEffect(() => {
    if (slateIsShowdown && contestType !== "SHOWDOWN") {
      applyContestType("SHOWDOWN");
      toast.info("Single-game slate loaded — switched to Showdown (Captain mode).");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slateIsShowdown]);
  // Sprint 62.1 — bankroll-aware sizing (informational + one-click suggest).
  const _DEFAULT_FEE = { MILLY: 20, GPP: 20, CASH: 10, SHOWDOWN: 5 };
  const [entryFee, setEntryFee] = useState(20);
  const [bankroll, setBankroll] = useState(() => {
    try { const v = Number(localStorage.getItem("wp_bankroll")); return v > 0 ? v : ""; } catch { return ""; }
  });
  useEffect(() => {
    try { if (Number(bankroll) > 0) localStorage.setItem("wp_bankroll", String(bankroll)); } catch { /* private mode */ }
  }, [bankroll]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isCash = contestType === "CASH";
  const riskCapPct = isCash ? 20 : 10;
  const atRisk = (Number(numLineups) || 0) * (Number(entryFee) || 0);
  const riskPct = Number(bankroll) > 0 ? (atRisk / Number(bankroll)) * 100 : null;
  const suggestedEntries = Number(bankroll) > 0 && Number(entryFee) > 0
    ? Math.max(1, Math.min(Number(maxEntries) || 1, Math.floor((Number(bankroll) * (isCash ? 0.10 : 0.05)) / Number(entryFee))))
    : null;
  // Track whether the user has manually edited the lineup count — once they
  // have, we stop stomping it with DEFAULT_LINEUPS[preset] on preset changes.
  // Fixes the "requested 150 but got 20" report: users would type 150, then
  // click a preset chip to sync settings, and the effect would silently reset
  // numLineups to that preset's default (20 for small_gpp / 20_max), sending
  // the reduced count to the optimizer without the user noticing.
  const [userEditedLineups, setUserEditedLineups] = useState(false);
  // Broadcast the live contest target so sibling banners (Strategy Mode,
  // Slate Mode) render the contest the user is actually building for
  // instead of the last-saved preset.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent("wp:contest-target-change", {
        detail: { contestType, fieldSize: Number(fieldSize) || null, maxEntries: Number(maxEntries) || null },
      }));
    } catch (_) { /* CustomEvent unsupported */ }
  }, [contestType, fieldSize, maxEntries]);
  const [busy, setBusy] = useState(false);
  // Sprint 103 — an Auto-Sense auto-build owns the engine; say so instead of
  // pretending the Smart optimizer is the thing running.
  const [autoSenseBusy, setAutoSenseBusy] = useState(false);
  // Sprint 62.8 — Coverage Guard: refresh field-model coverage when a build finishes / on mount.
  const { user: authUser } = useAuth() || {};
  const { coverage, ensureCoverage } = useFieldCoverage(busy);
  const [coverageGateOpen, setCoverageGateOpen] = useState(false);
  const coverageAckRef = useRef(false);
  const [poolReady, setPoolReady] = useState(false);
  // `poolChecked` gates the empty-state UI: don't flash "Slate not loaded yet"
  // until we've actually made the fetch. Without this the banner briefly
  // renders every remount (tab switch, hot reload) because poolReady starts
  // false — creating the misleading "no slate loaded" flicker users reported.
  const [poolChecked, setPoolChecked] = useState(false);
  const [pool, setPool] = useState([]);
  const [gateOpen, setGateOpen] = useState(false);
  // Sprint 60.15 — Turbo Mode toggle. Sprint 60.63 flipped the default
  // to ON after users reported 10-15 minute wallclock runs on 150-lineup
  // portfolios — sequential mode was the bottleneck, not the ILP itself.
  // 4-way parallel batching is 3-4× faster and Cluster Rotation still
  // dedupes near-duplicates at the tail, so portfolio quality stays
  // intact. Legacy users who never touched the toggle (`raw === null`)
  // get the upgrade automatically; users who explicitly set "0" stay
  // sequential — their preference is preserved. Persisted to
  // `wp_turbo_mode` so the choice sticks across tab switches / reloads.
  const [turboMode, setTurboMode] = useState(() => {
    try {
      const raw = localStorage.getItem("wp_turbo_mode");
      if (raw === null) return true;   // new default: Turbo ON
      return raw === "1";
    } catch { return true; }
  });
  const setTurboModePersisted = (v) => {
    setTurboMode(v);
    try { localStorage.setItem("wp_turbo_mode", v ? "1" : "0"); }
    catch { /* private mode safe */ }
  };
  // Punt-spine cap (Showdown): tighter any-slot cap for cheap bodies.
  const [puntCaps, setPuntCaps] = useState(() => {
    const d = { under1k: 25, under3k: 35, under5k: 40, maxCheap: 1 };
    try { const v = JSON.parse(localStorage.getItem("wp_punt_caps") || "null"); return v && typeof v === "object" ? { under1k: Number(v.under1k) || 25, under3k: Number(v.under3k) || 35, under5k: Number(v.under5k) || 40, maxCheap: Number.isFinite(Number(v.maxCheap)) ? Number(v.maxCheap) : 1 } : d; }
    catch { return d; }
  });
  const setPuntCap = (patch) => {
    const next = { ...puntCaps };
    for (const [k, raw] of Object.entries(patch)) next[k] = k === "maxCheap" ? Math.max(0, Math.min(6, Math.round(Number(raw) || 0))) : Math.max(1, Math.min(100, Number(raw) || 0));
    setPuntCaps(next);
    try { localStorage.setItem("wp_punt_caps", JSON.stringify(next)); } catch { /* private mode safe */ }
  };
  // FLEX share rules (Classic only): min/max % of lineups whose FLEX slot
  // is a 3rd RB / 4th WR / 2nd TE. Persisted per browser like the punt caps.
  const [flexCaps, setFlexCaps] = useState(() => {
    try { return normalizeFlexCaps(JSON.parse(localStorage.getItem("wp_flex_caps") || "null")); }
    catch { return { ...FLEX_CAPS_DEFAULT }; }
  });
  const setFlexCapsPersisted = (next) => {
    const n = normalizeFlexCaps(next);
    setFlexCaps(n);
    try { localStorage.setItem("wp_flex_caps", JSON.stringify(n)); } catch { /* private mode safe */ }
  };
  const flexIssue = contestType === "SHOWDOWN" ? null : flexCapsIssue(flexCaps);
  // Shadow field kill switch (parity with the Manual optimizer's edge
  // layers). ON by default — evading the field's stock-optimizer lineups is
  // the core edge; OFF gives a pure projection/EV build.
  const [shadowField, setShadowField] = useState(() => {
    try { return localStorage.getItem("wp_shadow_field") !== "0"; } catch { return true; }
  });
  const setShadowFieldPersisted = (v) => {
    setShadowField(v);
    try { localStorage.setItem("wp_shadow_field", v ? "1" : "0"); } catch { /* private mode safe */ }
  };
  // Alias kept for the request payload builder + progress labels
  // downstream so we didn't have to rename `fastMode` in every site.
  const fastMode = turboMode;
  // Stop-optimizing flow: `stopRequested` flips once the user clicks
  // Stop and stays true until the polling loop sees phase !== running.
  // `stopping` is a short in-flight guard for the POST itself to prevent
  // rapid double-taps from spamming the endpoint. Reset in the polling
  // teardown when phase transitions to done/error/idle.
  const [stopping, setStopping] = useState(false);
  // Sprint 60.45 — Force-Stop counter. If cooperative Stop doesn't
  // resolve within ~30s the second click sends `?force=true` which
  // hard-aborts the run so the user isn't stuck watching "Stopping…"
  // forever if the ILP thread wedges (rare but possible under
  // heavy CPU contention or a Mongo network stall).
  const [stopRequestedAt, setStopRequestedAt] = useState(null);
  const [stopRequested, setStopRequested] = useState(false);
  // Sprint 57 — mirror of localStorage.wp_excluded_games so we can render
  // a live "N games excluded" chip near the Generate button. Kept in sync
  // via the `storage` event (cross-tab) and a custom `wp:excluded-games`
  // event fired by GameCardGrid on toggle (same-tab).
  const [excludedGamesCount, setExcludedGamesCount] = useState(() => {
    try {
      const raw = localStorage.getItem("wp_excluded_games");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.length : 0;
    } catch { return 0; }
  });
  useEffect(() => {
    const sync = () => {
      try {
        const raw = localStorage.getItem("wp_excluded_games");
        const arr = raw ? JSON.parse(raw) : [];
        setExcludedGamesCount(Array.isArray(arr) ? arr.length : 0);
      } catch { setExcludedGamesCount(0); }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("wp:excluded-games", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("wp:excluded-games", sync);
    };
  }, []);
  // Live progress from `/api/optimize/progress` polled every 800ms while
  // `busy=true`. Powers the live progress bar so the 30–90s generation
  // no longer feels like a black box. Shape mirrors the backend field.
  const [progress, setProgress] = useState(null);
  // Monotonic UI progress ratchet — the raw backend phase transitions
  // legitimately reset `current` to 0 (queued → solving → simulating each
  // start counting from 0), which would make the visible bar snap
  // backwards. We ratchet a derived 0-100 pct that only ever advances,
  // giving users a smooth "steady forward motion" experience even across
  // phase transitions. Reset in `run` at the start of each new generation.
  const uiPctRatchetRef = useRef(0);
  const [uiPct, setUiPct] = useState(0);

  useEffect(() => {
    const isProjected = (p) => {
      if (p.unplayable_reason) return false;   // backup QB / injury / roster gate → deliberately zero
      const cands = [p.our_proj, p.projection, p.market_mean_proj, p.avg_ppg];
      return cands.some((v) => v != null && Number(v) > 0);
    };
    const fetchPool = () =>
      api.getPool(500).then((r) => {
        // Use "first positive" instead of `??` — during offseason, real integer
        // zero on `market_mean_proj` used to trip nullish coalescing and drop
        // every row from the pool. avg_ppg is the season-long backup projection.
        const rows = (r?.rows || []).filter(isProjected);
        setPool(rows);
        setPoolReady(rows.length > 0);
      })
      .catch(() => setPoolReady(false))
      .finally(() => setPoolChecked(true));

    api.contestPresets?.().then((r) => setPresetMap(r?.presets || {})).catch(() => {});
    fetchPool();

    // Re-sync pool state whenever the app broadcasts a pool refresh (mock
    // slate load, DK CSV upload, DK auto-ingester tick, etc). Without this,
    // SundayAutoMode's poolReady would go stale after any upstream mutation
    // and the "Slate not loaded" warning would flash even though the app-wide
    // pool is actually populated. Fixes the post-optimize glitch where users
    // saw the warning even though generation just succeeded on the same pool.
    const onPoolRefreshed = () => fetchPool();
    window.addEventListener("wp:pool-refreshed", onPoolRefreshed);

    // Sprint 60.4 — Resume polling if an optimize run is already in
    // flight when we mount. Fixes the "user navigates away mid-run →
    // comes back → sees no progress" bug. The BACKEND thread keeps
    // running (see routes/optimize.py threading.Thread) but the polling
    // loop lives inside this component and cleans up on unmount. On
    // remount we consult the Mongo-backed /optimize/progress endpoint;
    // if it reports an active phase (queued / preparing / solving /
    // simulating / rotating) we set busy=true and re-start polling so
    // the user sees exactly what phase & count they're at.
    let resumePoll = null;
    let resumeAborted = false;
    (async () => {
      try {
        const p = await api.optimizeProgress();
        if (resumeAborted) return;
        const active = ["queued", "preparing", "solving", "simulating", "rotating"].includes(p?.phase);
        if (!active) return;
        // Sprint 103 — an Auto-Sense auto-build writes the same progress doc.
        // Resuming it here made the Smart panel claim "Building your N-lineup
        // portfolio" while Auto-Sense was the thing actually running, and then
        // announce a finished portfolio that was never Smart's. Leave it to
        // the Auto-Sense panel and just say so.
        if (p?.kind === "autosense" || String(p?.phase_label || "").startsWith("Auto-build")) {
          setAutoSenseBusy(true);
          resumePoll = setInterval(async () => {
            try {
              const np = await api.optimizeProgress();
              if (np?.kind !== "autosense" || np?.phase === "done" || np?.phase === "error") {
                clearInterval(resumePoll);
                resumePoll = null;
                setAutoSenseBusy(false);
              }
            } catch { /* transient — keep polling */ }
          }, 1500);
          return;
        }
        setBusy(true);
        setProgress(p);
        resumePoll = setInterval(async () => {
          try {
            const np = await api.optimizeProgress();
            setProgress(np);
            if (np?.phase === "done" || np?.phase === "error") {
              clearInterval(resumePoll);
              resumePoll = null;
              setBusy(false);
              setStopRequested(false);
              if (np?.phase === "done") {
                toast.success(`Portfolio ready — ${np.current || 0} lineups`, {
                  description: "Open the Lineups tab to view them.",
                });
                try { window.dispatchEvent(new CustomEvent("wp:pool-refreshed")); } catch { /* noop */ }
              }
            }
          } catch { /* transient — keep polling */ }
        }, 1000);
      } catch { /* not signed in, or endpoint transient — silent */ }
    })();

    return () => {
      window.removeEventListener("wp:pool-refreshed", onPoolRefreshed);
      resumeAborted = true;
      if (resumePoll) clearInterval(resumePoll);
    };
  }, []);

  // Sync preset choice across the app
  useEffect(() => {
    try { localStorage.setItem("wp_active_preset", preset); } catch (e) { /* private mode */ }
    try {
      window.dispatchEvent(new CustomEvent("wp:preset-change", { detail: { preset, source: "sunday-auto" } }));
    } catch (e) { /* noop */ }
    // Only seed the count from the preset default if the user hasn't
    // typed their own value. Prevents the "silent 150 -> 20 reset" bug.
    if (!userEditedLineups) {
      setNumLineups(DEFAULT_LINEUPS[preset] || 20);
    }
  }, [preset, userEditedLineups]);

  // Actually runs the optimizer with a finalized override map. Called either
  // (a) directly when the user already has ≥2 touches, or (b) from the
  // TwoTouchGate confirm callback with the merged overrides it produced.
  const runOptimize = async (mergedOverrides, gateInfo) => {
    setBusy(true);
    // Sprint 60.6 — one-time friendly heads-up that the run persists
    // across tab navigation. We used to have users get anxious when
    // they clicked away and saw no progress — now the backend thread
    // keeps running and this component resumes polling on remount.
    // The toast is silenceable per-browser via localStorage so it
    // doesn't nag return users.
    try {
      if (!localStorage.getItem("wp_optimize_nav_toast_dismissed")) {
        toast.info("Optimization runs in the background", {
          description: "Feel free to browse other tabs — your run keeps going and progress will resume here when you come back.",
          duration: 6500,
          action: {
            label: "Got it",
            onClick: () => {
              try { localStorage.setItem("wp_optimize_nav_toast_dismissed", "1"); } catch { /* private */ }
            },
          },
        });
      }
    } catch { /* toast is nice-to-have */ }
    // Reset UI progress ratchet at the START of each new generation only.
    // Within a generation, the ratchet only advances (see poll handler
    // below) — this is what makes the loading bar feel smooth even when
    // the backend legitimately resets `current` to 0 across phase
    // transitions (queued → solving → simulating each start counting
    // from 0 on their own axis).
    uiPctRatchetRef.current = 0;
    setUiPct(0);
    setProgress({ current: 0, total: numLineups, phase: "queued", phase_label: "Queued · warming up rule engine", elapsed: 0 });

    // ─── Async-job pattern (Cloudflare 100s timeout mitigation) ─────────
    // POST /api/optimize now returns 202 immediately after kicking off a
    // background task. We poll /api/optimize/progress every 800ms until the
    // backend signals `phase in ("done", "error")`, then finalize.
    // Previously we awaited the full 45–90s POST response, which sometimes
    // exceeded Cloudflare's 100s origin timeout on the Free plan and threw
    // a spurious "origin didn't respond" popup mid-flight.

    let pollId = null;
    try {
      // Sprint 57 — game-level exclusion. Read the set of games the user
      // locked out via the Game Card Grid (persisted to localStorage) and
      // forward them to the backend so those games' players are filtered
      // out of the pool BEFORE the ILP runs. Empty / missing = no
      // exclusions (default behavior).
      let excludedGames = [];
      try {
        const raw = localStorage.getItem("wp_excluded_games");
        excludedGames = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(excludedGames)) excludedGames = [];
      } catch { excludedGames = []; }

      const cfg = {
        num_lineups: Number(numLineupsRef.current) || Number(numLineups) || 1,
        contest_preset: preset,
        // Sprint 60.68 — new simplified contest picker. Backend
        // derive_config uses these to build rule numbers dynamically
        // (bypasses the per-field values on the request). Only send
        // when the user's picker matches a supported contest type.
        contest_type: contestType === "CASH" ? cashSubType : contestType,
        entry_fee: Number(entryFee) || null,
        dk_contest_id: dkContestId,
        // Sprint 60.141 — coerce anti-clamp inputs to sane numbers just
        // before dispatch. Users can transiently see empty/small values
        // while typing (e.g. "1" en route to "115000") — enforce the
        // real min only when firing the ILP.
        field_size: Number.isFinite(Number(fieldSize)) && Number(fieldSize) >= 2 ? Number(fieldSize) : 2,
        max_entries_per_user: Number.isFinite(Number(maxEntries)) && Number(maxEntries) >= 1 ? Number(maxEntries) : 1,
        player_overrides: mergedOverrides,
        parallel_batches: fastMode ? 4 : 1,
        shadow_field_enabled: shadowField,
        excluded_games: excludedGames,
        cpt_max_share_pct: contestType === "SHOWDOWN" && cptShareRef.current ? cptShareRef.current : undefined,
        punt_cap_under_1k_pct: contestType === "SHOWDOWN" ? puntCaps.under1k : undefined,
        punt_cap_under_3k_pct: contestType === "SHOWDOWN" ? puntCaps.under3k : undefined,
        punt_cap_under_5k_pct: contestType === "SHOWDOWN" ? puntCaps.under5k : undefined,
        max_cheap_bodies: contestType === "SHOWDOWN" ? puntCaps.maxCheap : undefined,
        flex_exposure_caps: contestType === "SHOWDOWN" ? undefined : (flexCapsPayload(flexCaps) || undefined),
      };
      // Fire the job (returns 202 with {status: "started"} in <1s).
      await api.optimize(cfg);

      // Optimize accepted — the backend just validated the pool. Force
      // poolReady so the footer never falsely shows "Slate not loaded".
      setPoolReady(true);
      setPoolChecked(true);

      // Poll for progress until terminal.
      // Watchdog: if we've been stuck in "queued" for > 30 seconds without
      // ever advancing to "preparing", the background task died silently
      // (e.g. GC'd, OOM, or crashed before flipping phase). Abort with a
      // real error so the finally block resets the UI instead of spinning
      // forever at 5%.
      const jobStartedAt = Date.now();
      let sawNonQueued = false;
      const QUEUED_WATCHDOG_MS = 30_000;

      const terminalProgress = await new Promise((resolve, reject) => {
        pollId = setInterval(async () => {
          try {
            const p = await api.optimizeProgress();
            setProgress(p);
            if (p?.phase && p.phase !== "queued") sawNonQueued = true;
            // Watchdog trip: still in queued after 30s → dead task.
            if (!sawNonQueued && (Date.now() - jobStartedAt) > QUEUED_WATCHDOG_MS) {
              clearInterval(pollId); pollId = null;
              reject({ response: { data: { detail: "Generation didn't start (task never advanced past 'queued'). This usually means the backend task crashed silently — please try again." } } });
              return;
            }
            // ─── Monotonic UI ratchet ─────────────────────────────
            // Map (phase, current/total) to a global 0-100 pct that
            // spans the full pipeline. Ratchet: only advance forward.
            //   preparing/queued : 0-3%
            //   solving          : 3-72%   (bulk — ILP loop)
            //   simulating       : 72-93%  (Monte Carlo)
            //   rotating         : 93-99%  (Cluster Rotation)
            //   done             : 100%
            const cur = Number(p?.current || 0);
            const tot = Number(p?.total || 1);
            const frac = tot > 0 ? Math.min(1, cur / tot) : 0;
            let raw = 0;
            switch (p?.phase) {
              case "queued":
              case "preparing": raw = 3 * frac; break;
              case "solving":   raw = 3 + 69 * frac; break;
              case "simulating": raw = 72 + 21 * frac; break;
              case "rotating":   raw = 93 + 6 * frac; break;
              case "done":       raw = 100; break;
              case "error":      raw = uiPctRatchetRef.current; break;
              default:           raw = uiPctRatchetRef.current;
            }
            if (raw > uiPctRatchetRef.current) {
              uiPctRatchetRef.current = raw;
              setUiPct(raw);
            }
            if (p?.phase === "done") { clearInterval(pollId); pollId = null; resolve(p); }
            else if (p?.phase === "error") {
              clearInterval(pollId); pollId = null;
              reject({ response: { data: { detail: p?.error?.detail || "Generation failed" } } });
            }
          } catch (_) { /* transient poll error — keep trying */ }
        }, 800);
      });

      // Fetch the final lineup payload for the shortfall/success toast.
      const finalRes = await api.getLineups();
      const userTouchCount = Object.keys(mergedOverrides || {}).length;
      const requested = Number(terminalProgress.requested || finalRes.requested || numLineups);
      const count = Number(finalRes.count || 0);
      const shortfall = requested && count < requested;

      if (terminalProgress?.phase_label && String(terminalProgress.phase_label).startsWith("Stopped by user")) {
        // User pulled the ripcord mid-solve. Any completed lineups are
        // preserved server-side and just fetched via getLineups above.
        toast.success(
          `Stopped · kept ${count} lineup${count === 1 ? "" : "s"}`,
          {
            description: count > 0
              ? "Your partial portfolio landed in the Lineups tab. Nothing lost."
              : "No lineups had been built yet — try again with looser constraints or a smaller target count.",
            duration: 8000,
          }
        );
      } else if (shortfall) {
        toast.warning(
          `Generated ${count}/${requested} lineups`,
          {
            description: (terminalProgress.generation_notes && terminalProgress.generation_notes[0])
              || "Your Player Pool constraints were too tight to hit the requested count. Loosen a lock/exclude or lower Min Exp and try again.",
            duration: 10000,
          }
        );
      } else if (gateInfo) {
        toast.success(
          `Generated ${count} lineups · compliance touches applied`,
          { description: `${gateInfo.sleeper.name} +15% · ${gateInfo.anchor.name} capped at ${gateInfo.ceiling}%` }
        );
      } else {
        toast.success(
          `Generated ${count} lineups${userTouchCount > 0 ? ` · ${userTouchCount} of your Player Pool choices applied` : ""}`
        );
      }
      if (typeof onGenerated === "function") onGenerated({ count, requested, ...finalRes });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const code = typeof d === "object" ? d?.code : null;
      const msg = typeof d === "string" ? d : d?.message || err?.message || "Generation failed";
      if (code === "POOL_STATE_STALE") {
        toast.error(msg, { duration: 12000 });
      } else if (code === "OPTIMIZE_ALREADY_RUNNING") {
        toast.warning(msg, { duration: 8000 });
      } else {
        toast.error(msg);
      }
    } finally {
      if (pollId) clearInterval(pollId);
      setBusy(false);
      setStopRequested(false);
      setStopping(false);
      setTimeout(() => setProgress(null), 1500);
    }
  };

  // DT gate — before we let a build start, verify the user has consciously
  // flagged at least one QB as Dual-Threat. Without a DT flag, the Combat
  // Stack Matrix has no valid target for `primary_only` (naked QB, no
  // receiver) plays and quietly downgrades that portion of the schedule to
  // P+S — which changes the portfolio construction in ways users may not
  // realize. Better to force a conscious decision every week: "Which QBs
  // are dual-threat this week?" (rushing-heavy Josh Allen / Lamar / Hurts
  // tier). Users can also confirm "no DTs this week" and continue.
  const [dtGateOpen, setDtGateOpen] = useState(false);
  const hasDtFlag = () => {
    const o = userOverrides || {};
    for (const key of Object.keys(o)) {
      if (o[key]?.dual_threat === true) return true;
    }
    return false;
  };

  const run = async () => {
    if (cashSubTypeMissing) {
      toast.error("Pick a cash format (Double-Up, 50-50 or Head-to-Head) first.");
      return;
    }
    if (showdownMismatch) {
      toast.error("Showdown needs a single-game slate — pick a TNF / SNF / MNF slate in Data Feeds.");
      return;
    }
    if (!poolReady || pool.length < 6) {
      toast.error("Slate not loaded yet. Give the DK auto-ingester a moment and refresh.");
      return;
    }
    // Sprint 62.8 — Coverage Guard: thin competitor coverage → one-click confirm (once per session).
    if (!coverageAckRef.current && needsCoverageConfirm(await ensureCoverage(), authUser?.is_admin)) {
      setCoverageGateOpen(true);
      return;
    }
    // DT gate — only fires if the pool has QBs but the user hasn't flagged
    // any DT for this week. Cheap heuristic; won't block valid slates like
    // Showdown pool-of-1-QB where a DT flag doesn't matter.
    const qbCount = pool.filter((p) => p.position === "QB").length;
    if (qbCount >= 3 && !hasDtFlag()) {
      setDtGateOpen(true);
      return;
    }
    // If the user has already made ≥2 real touches in Pro Mode, we honor
    // those and go straight to generation — no need for the gate.
    if (countSubstantiveTouches(userOverrides) >= 2) {
      runOptimize(userOverrides, null);
      return;
    }
    // Otherwise open the compliance gate.
    setGateOpen(true);
  };

  runRef.current = run;

  // "I've flagged my DTs" confirmation from the DT gate → resume normal
  // Generate flow. "No DTs this week" confirmation from the gate → same
  // resumption path (the user's conscious "none" is a valid answer).
  const onDtGateContinue = () => {
    setDtGateOpen(false);
    // Resume the normal Generate flow — check compliance touch count and
    // either proceed straight to optimize or open the two-touch gate.
    setTimeout(() => {
      if (countSubstantiveTouches(userOverrides) >= 2) {
        runOptimize(userOverrides, null);
      } else {
        setGateOpen(true);
      }
    }, 0);
  };
  const onDtGateClose = () => setDtGateOpen(false);

  const onGateConfirm = (mergedOverrides, gateInfo) => {
    setGateOpen(false);
    // Propagate the gate's real user choices up to the parent Optimizer state
    // so the ComplianceBanner + Pro Mode Generate button reflect the true
    // touch count. Without this, the ComplianceBanner would keep saying "0/2"
    // even after the user cleared the gate — confusing them into thinking
    // compliance failed when it actually succeeded server-side.
    if (typeof onOverridesChange === "function") {
      onOverridesChange(mergedOverrides);
    }
    runOptimize(mergedOverrides, gateInfo);
  };

  // Mock slate loader removed in Sprint 52 — product is live-testing on real
  // DK slates only. Users load slates via the /uploads DK auto-ingest chips
  // or manual CSV upload.

  // Stop the in-flight optimize task. Cooperative — backend flips a flag
  // that the ILP loop polls between lineups; the current lineup finishes,
  // everything built so far is serialized and Monte Carloed, and the
  // portfolio lands in the Lineups tab as usual. UI stays in "busy" state
  // until the polling loop sees phase transition to done so the user sees
  // the same completion animation as a normal run — just with a "Stopped
  // by user" phase label. Partial builds are a first-class outcome.
  const stop = async () => {
    if (stopping) return;
    // Sprint 60.49 — When the user clicks the button that literally
    // says "Force Stop" (i.e. after any prior stop request), send
    // force=true immediately. No 30-second waiting period, no timestamp
    // dance. The prior 30s gate produced the exact bug reported on
    // prod: 3 stacked "Stop requested" toasts because forceMode
    // was always false. If they wanted to be cautious they'd have
    // waited for the first stop to work; clicking the yellow button
    // labeled "Force Stop" IS the cautious-user's second try.
    const forceMode = stopRequested === true;
    setStopping(true);
    try {
      const r = await api.optimizeStop(forceMode);
      if (r?.stopped) {
        setStopRequested(true);
        if (!stopRequestedAt) setStopRequestedAt(Date.now());
        if (forceMode || r.force) {
          toast.warning(
            `Force-stopped · ${r.current || 0} lineups kept`,
            {
              description: "The optimizer was hard-aborted. Your partial portfolio lands in the Lineups tab. Click Generate to run again.",
              duration: 10000,
            }
          );
          // Force-stop already marks phase=done — kick the UI out of busy.
          setBusy(false);
          setProgress(null);
        } else {
          toast.info(
            `Stop requested — finalizing lineup ${r.current || 0} of ${r.total || 0}`,
            {
              description: "Every lineup built so far will be saved. If this doesn't clear in 30 seconds, click Stop again to force-abort.",
              duration: 10000,
            }
          );
        }
      } else {
        // Backend confirms no task is running, but the UI shows "Generating…"
        // — this happens when the backend task silently died (e.g. was GC'd
        // on prod before advancing phase from "queued") and the frontend
        // polling loop stayed stuck on the seed progress. Force-reset the
        // whole UI so the user can retry immediately instead of being
        // trapped behind a permanently-spinning button.
        toast.warning(
          "The optimizer isn't running anymore — resetting so you can retry.",
          {
            description: "This can happen if a previous run crashed silently. Click Generate to start fresh.",
            duration: 8000,
          }
        );
        setBusy(false);
        setStopRequested(false);
        setStopRequestedAt(null);
        setProgress(null);
        uiPctRatchetRef.current = 0;
        setUiPct(0);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reach the stop endpoint");
    } finally {
      setStopping(false);
    }
  };

  return (
    <section
      data-testid="sunday-auto-section"
      className="relative overflow-hidden border-2 border-[#39FF14] bg-gradient-to-br from-[#0a1a10] via-[#0b0b0b] to-[#0a0a0a]"
      style={{ boxShadow: "0 0 40px rgba(57,255,20,0.15), inset 0 1px 0 rgba(255,255,255,0.04)" }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-50"
           style={{ background: "radial-gradient(700px 180px at 15% 0%, rgba(57,255,20,0.20), transparent 70%)" }} />
      <div className="relative px-5 py-5">
        {authUser?.is_admin && <div className="mb-4"><PreflightPanel compact /></div>}
        {/* Sprint 60.68 — Simplified 4-way contest picker.
            Replaces the old preset dropdown for the primary flow.
            Field-size + max-entries feed derive_config on the backend
            so users never see (and can't leak) individual rule numbers. */}
        <div className="mb-4 pb-4 border-b border-zinc-800" data-testid="contest-picker">
          <div className="mono text-[9px] uppercase tracking-[0.28em] text-zinc-500 mb-2">
            Contest
          </div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {["MILLY", "GPP", "CASH", "SHOWDOWN"].map((ct) => (
              <button
                key={ct}
                onClick={() => applyContestType(ct)}
                data-testid={`contest-${ct.toLowerCase()}`}
                className={`px-2 py-2 text-[10px] mono uppercase tracking-widest transition-colors border ${
                  contestType === ct
                    ? "bg-[#39FF14] text-black border-[#39FF14] font-black"
                    : "bg-black text-zinc-400 border-zinc-800 hover:border-zinc-600"
                }`}
              >
                {ct === "MILLY" ? "Milly Maker" : ct === "SHOWDOWN" ? "Showdown" : ct}
              </button>
            ))}
          </div>
          {showdownMismatch && (
            <div className="mb-3 border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-3 py-2 text-[10px] mono text-[#FF3B30]" data-testid="showdown-slate-mismatch">
              Showdown builds need a single-game slate. Loaded slate: {slateType}. Pick a Showdown slate (TNF / SNF / MNF) in Data Feeds, or switch to Milly / GPP / Cash.
            </div>
          )}
          {slateIsShowdown && contestType === "SHOWDOWN" && (() => {
            const fs = Number(fieldSize) || 0;
            const tier = showdownTierOf(fs);
            return (
              <div className="mb-3 border border-[#FF8A00]/60 bg-[#FF8A00]/10 px-3 py-2 text-[10px] mono text-[#FF8A00]" data-testid="showdown-slate-ready">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-400 uppercase tracking-widest text-[9px]">Attacking</span>
                  <span className="font-black text-[11px] text-white bg-[#FF8A00] px-1.5 py-0.5" data-testid="showdown-attacking-tier">{tier.name}</span>
                  <span className="text-zinc-300">{fs ? `${fs.toLocaleString()} entries` : "set a field size"} · {tier.rules}{tier.blendNote}</span>
                </div>
                <div className="mt-1 text-[#FF8A00]/80">Captain mode · 1 CPT (1.5× salary &amp; points) + 5 FLEX · pin or ban captains in the Player Pool below. Rules re-scale the moment you change the field size.</div>
              </div>
            );
          })()}
          {contestType === "CASH" && (
            <div className="mb-3" data-testid="cash-subtype-picker">
              <div className="mono text-[9px] uppercase tracking-[0.28em] text-zinc-500 mb-2">
                Cash Format <span className="text-[#FF3B30]">· required</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {CASH_SUBTYPES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => applyCashSubType(s.key)}
                    data-testid={`cash-sub-${s.key.toLowerCase()}`}
                    className={`px-2 py-2 text-left border transition-colors ${
                      cashSubType === s.key
                        ? "bg-[#FFCC00] text-black border-[#FFCC00]"
                        : "bg-black text-zinc-400 border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <div className="text-[10px] mono uppercase tracking-widest font-black">{s.label}</div>
                    <div className={`text-[9px] mono mt-0.5 ${cashSubType === s.key ? "text-black/70" : "text-zinc-500"}`}>{s.hint}</div>
                  </button>
                ))}
              </div>
              {cashSubTypeMissing && (
                <div className="text-[9px] mono text-[#FF3B30] mt-1.5" data-testid="cash-subtype-required">
                  Pick a cash format — the payout curve decides the ruleset.
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mb-3" data-testid="dk-contest-pull">
            <input
              type="text"
              value={dkContestRef}
              onChange={(e) => setDkContestRef(e.target.value)}
              placeholder="DK contest ID or URL — auto-fill field size, max entries & fee"
              data-testid="dk-contest-ref"
              className="flex-1 bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-[11px] mono"
            />
            <button
              type="button"
              onClick={pullDkContest}
              disabled={dkPulling || busy}
              data-testid="dk-contest-pull-btn"
              className="px-3 py-1.5 text-[10px] mono uppercase tracking-widest border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14] hover:text-black disabled:opacity-50 transition-colors font-bold"
            >
              {dkPulling ? "Pulling…" : "Pull from DK"}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <label className="block">
              <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">
                Field Size
              </div>
              <input
                type="number"
                value={fieldSize}
                onChange={(e) => {
                  // Sprint 60.141 — Don't clamp on every keystroke. Users
                  // typing "115000" would type "1" first, which the old
                  // `Math.max(2, +v || 2)` immediately promoted to "2",
                  // effectively locking the leading digit. Store whatever
                  // the user types (allow blank/0/1 as transient state),
                  // clamp to the min on blur / submit.
                  const raw = e.target.value;
                  if (raw === "") { setFieldSize(""); return; }
                  const n = Number(raw);
                  if (Number.isNaN(n)) return;
                  setFieldSize(n);
                }}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isFinite(n) || n < 2) setFieldSize(2);
                }}
                data-testid="contest-field-size"
                className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
              />
              {contestType === "MILLY" && (
                <div className="text-[9px] text-zinc-500 mt-1 leading-relaxed">
                  ~150k–200k at the $20 flagship · 400k+ at the $5 opener.
                </div>
              )}
              {contestType === "SHOWDOWN" && (
                <div className="text-[9px] text-zinc-500 mt-1 leading-relaxed" data-testid="showdown-tier-hint">
                  {(() => { const t = showdownTierOf(fieldSize); return `${t.name.replace("SHOWDOWN ", "")} rules · ${t.rules}${t.blendNote}`; })()}
                </div>
              )}
            </label>
            <label className="block">
              <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">
                Max Entries / User
              </div>
              <input
                type="number"
                value={maxEntries}
                onChange={(e) => {
                  // Same anti-clamp-on-keystroke pattern — let the user
                  // type intermediate values freely, coerce on blur.
                  const raw = e.target.value;
                  if (raw === "") { setMaxEntries(""); return; }
                  const n = Number(raw);
                  if (Number.isNaN(n)) return;
                  setMaxEntries(n);
                }}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isFinite(n) || n < 1) setMaxEntries(1);
                }}
                data-testid="contest-max-entries"
                className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
              />
            </label>
            <label className="block">
              <div className="text-[9px] mono uppercase tracking-widest text-[#39FF14] mb-1 font-bold">
                Your Entries
              </div>
              <input
                type="number"
                min={1}
                max={500}
                value={numLineups}
                onChange={(e) => {
                  setUserEditedLineups(true);
                  setNumLineups(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1)));
                }}
                disabled={busy}
                data-testid="sunday-auto-count"
                className="w-full bg-black border border-[#39FF14]/60 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono text-[#39FF14] font-bold"
              />
            </label>
            <label className="block">
              <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">
                Entry Fee ($)
              </div>
              <input
                type="number"
                min={0}
                step="1"
                value={entryFee}
                onChange={(e) => setEntryFee(e.target.value === "" ? "" : Number(e.target.value))}
                data-testid="contest-entry-fee"
                className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
              />
            </label>
            <label className="block">
              <div className="text-[9px] mono uppercase tracking-widest text-zinc-500 mb-1">
                Bankroll ($)
              </div>
              <input
                type="number"
                min={0}
                step="50"
                value={bankroll}
                placeholder="optional"
                onChange={(e) => setBankroll(e.target.value === "" ? "" : Number(e.target.value))}
                data-testid="contest-bankroll"
                className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono placeholder:text-zinc-700"
              />
            </label>
          </div>
          <div
            className="mt-2 flex items-center justify-between gap-3 flex-wrap text-[10px] mono"
            data-testid="bankroll-strip"
          >
            <div className="text-zinc-400">
              At risk: <span className="text-white font-bold" data-testid="bankroll-at-risk">${atRisk.toLocaleString()}</span>
              {riskPct != null && (
                <>
                  {" "}· <span
                    data-testid="bankroll-risk-pct"
                    className={riskPct > riskCapPct ? "text-[#FF3B30] font-bold" : riskPct > riskCapPct * 0.7 ? "text-[#FFCC00]" : "text-[#39FF14]"}
                  >
                    {riskPct.toFixed(1)}% of bankroll
                  </span>
                  {riskPct > riskCapPct && (
                    <span className="text-[#FF3B30]"> — over the {riskCapPct}% {isCash ? "cash" : "tournament"} guideline</span>
                  )}
                </>
              )}
              {riskPct == null && <span className="text-zinc-600"> · add a bankroll to size entries</span>}
            </div>
            {suggestedEntries != null && suggestedEntries !== Number(numLineups) && (
              <button
                type="button"
                data-testid="bankroll-use-suggested"
                onClick={() => { setUserEditedLineups(true); setNumLineups(suggestedEntries); }}
                className="uppercase tracking-widest text-[#39FF14] hover:bg-[#39FF14] hover:text-black border border-[#39FF14]/60 px-2 py-0.5 transition-colors"
                title={`${isCash ? "10%" : "5%"} of bankroll ÷ entry fee, capped at the contest's max entries`}
              >
                Use {suggestedEntries} entries →
              </button>
            )}
            <span className="text-zinc-600 italic">Rules auto-scale to contest, field size and max entries.</span>
          </div>
        </div>
        {contestType !== "SHOWDOWN" && (
          <FlexShareRules caps={flexCaps} onChange={setFlexCapsPersisted} disabled={busy} />
        )}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-[#39FF14] text-black font-black flex items-center justify-center flex-shrink-0"
                 style={{ boxShadow: "0 0 12px rgba(57,255,20,0.6)" }}>
              <Rocket size={16} weight="fill" />
            </div>
            <div className="min-w-0">
              <h3 className="heading text-white font-black text-xl leading-tight">
                Pick your contest. Click. Ship it.
              </h3>
              <div className="mono text-[9px] uppercase tracking-widest mb-1">
                <FieldCoverageBadge coverage={coverage} />
              </div>
              <p className="text-[11px] text-zinc-400 leading-relaxed" data-testid="field-edge-summary">
                Contest-shaped stacks · correlated game scripts · steps around the field's chalk builds · dup cap ·
                Field-EV ranking — all scaled to your contest. Two Player Pool touches unlock the build.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Edge layer chip — same switch as the Manual optimizer, surfaced
                at top level so it is one tap away from Generate. */}
            <button
              type="button"
              role="switch"
              aria-checked={shadowField}
              onClick={() => setShadowFieldPersisted(!shadowField)}
              disabled={busy}
              data-testid="shadow-field-chip"
              title={shadowField
                ? "Shadow Field ON — builds step around the lineups stock optimizers produce. Click to turn off."
                : "Shadow Field OFF — pure projection / EV build with no field evasion. Click to turn on."}
              className={`inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${
                shadowField
                  ? "border-[#FF8A00] text-[#FF8A00] bg-[#FF8A00]/10 font-bold"
                  : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
              } ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Ghost size={12} weight="fill" />
              Shadow Field {shadowField ? "ON" : "OFF"}
            </button>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              data-testid="build-advanced-toggle"
              aria-expanded={showAdvanced}
              className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#39FF14] border border-zinc-800 hover:border-[#39FF14]/60 px-2.5 py-1.5 transition-colors"
            >
              <Sliders size={12} />
              Advanced {showAdvanced ? "▴" : "▾"}
            </button>
          </div>
        </div>

        {showAdvanced && (
        <div className="mt-4 border border-zinc-800 bg-black/30 p-3 space-y-4" data-testid="build-advanced-panel">
        <StrategyPicker
          presets={presetMap}
          active={preset}
          onPick={setPreset}
          disabled={busy}
          testIdPrefix="sunday-auto-strategy"
        />
        <ConfidenceCopy presetKey={
          {
            MILLY:    "milly_maker",
            GPP:      "20_max",
            CASH:     "cash_50_50",
            SHOWDOWN: "showdown",
          }[contestType] || preset
        } />

        {/* Turbo Mode toggle (Sprint 60.15 · Sprint 60.42 copy scrub).
            Opt-in 4-way parallel batching for 3-4× wall-clock speedup on
            production hardware. OFF = sequential single-thread solver
            (deterministic order); ON = parallel batches (Cluster Rotation
            still dedupes at the tail so portfolio quality stays intact).
            Copy never references "the book" — the proprietary ruleset
            IS the product, and calling it out sounds like we borrowed
            it from a public source. */}
        <div
          className="flex items-center justify-between gap-3 border border-zinc-800 bg-[#0e0e0e] px-3 py-2.5"
          data-testid="turbo-mode-row"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Lightning size={14} weight="fill" className={turboMode ? "text-[#FFCC00]" : "text-zinc-600"} />
            <div className="min-w-0">
              <div className="mono text-[11px] font-black uppercase tracking-widest">
                Turbo Mode
                <span className={`ml-2 mono text-[9px] px-1.5 py-0.5 border ${
                  turboMode
                    ? "border-[#FFCC00]/60 text-[#FFCC00] bg-[#FFCC00]/10"
                    : "border-zinc-800 text-zinc-500"
                }`}>
                  {turboMode ? "ON · 4× parallel · recommended" : "OFF · sequential · slower"}
                </span>
              </div>
              <div className="text-[10px] mono text-zinc-500 mt-0.5">
                {turboMode
                  ? "4-way parallel batching · 3-4× faster · dedupes at the tail"
                  : "Sequential fallback · deterministic order · ~4× slower — keep ON unless you need a reproducible run"}
              </div>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={turboMode}
            onClick={() => setTurboModePersisted(!turboMode)}
            disabled={busy}
            data-testid="turbo-mode-toggle"
            className={`relative w-11 h-6 flex-shrink-0 border transition-colors ${
              turboMode
                ? "bg-[#FFCC00] border-[#FFCC00]"
                : "bg-zinc-900 border-zinc-700 hover:border-zinc-500"
            } ${busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span
              className={`absolute top-[2px] w-4 h-4 transition-transform ${
                turboMode
                  ? "translate-x-[22px] bg-black"
                  : "translate-x-[2px] bg-zinc-400"
              }`}
            />
          </button>
        </div>
        {/* Shadow Field toggle — same edge layer the Manual optimizer exposes. */}
        <div
          className="flex items-center justify-between gap-3 border border-zinc-800 bg-[#0e0e0e] px-3 py-2.5"
          data-testid="shadow-field-row"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Ghost size={14} weight="fill" className={shadowField ? "text-[#FF8A00]" : "text-zinc-600"} />
            <div className="min-w-0">
              <div className="mono text-[11px] font-black uppercase tracking-widest">
                Shadow Field
                <span className={`ml-2 mono text-[9px] px-1.5 py-0.5 border ${
                  shadowField
                    ? "border-[#FF8A00]/60 text-[#FF8A00] bg-[#FF8A00]/10"
                    : "border-zinc-800 text-zinc-500"
                }`}>
                  {shadowField ? "ON · evading the field · recommended" : "OFF · pure projection build"}
                </span>
              </div>
              <div className="text-[10px] mono text-zinc-500 mt-0.5">
                {shadowField
                  ? "Simulates the lineups stock optimizers will spit out and steps around them, capping shared players and duplicate probability."
                  : "No field evasion — lineups are built purely on projection and EV, so expect more overlap with the field."}
              </div>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={shadowField}
            onClick={() => setShadowFieldPersisted(!shadowField)}
            disabled={busy}
            data-testid="shadow-field-toggle"
            className={`relative w-11 h-6 flex-shrink-0 border transition-colors ${
              shadowField
                ? "bg-[#FF8A00] border-[#FF8A00]"
                : "bg-zinc-900 border-zinc-700 hover:border-zinc-500"
            } ${busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span
              className={`absolute top-[2px] w-4 h-4 transition-transform ${
                shadowField ? "translate-x-[22px] bg-black" : "translate-x-[2px] bg-zinc-400"
              }`}
            />
          </button>
        </div>
        {contestType === "SHOWDOWN" && (
          <div className="border border-zinc-800 bg-[#0e0e0e] px-3 py-2.5" data-testid="punt-spine-cap-row">
            <div className="mono text-[11px] font-black uppercase tracking-widest">
              Punt Spine Cap
              <span className="ml-2 mono text-[9px] px-1.5 py-0.5 border border-[#FF8A00]/60 text-[#FF8A00] bg-[#FF8A00]/10">
                cheap bodies · max share of the portfolio
              </span>
            </div>
            <div className="text-[10px] mono text-zinc-500 mt-0.5">
              Stud-heavy pricing pushes the same $300–$900 fillers into every lineup. These caps hold each cheap body under a share of your build; everyone else keeps the tier's any-slot cap.
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-zinc-400">
                Under $1,000
                <input type="number" min="1" max="100" step="5" value={puntCaps.under1k} disabled={busy}
                       onChange={(e) => setPuntCap({ under1k: e.target.value })}
                       data-testid="punt-cap-under-1k"
                       className="w-16 bg-black border border-zinc-800 focus:border-[#FF8A00] outline-none px-2 py-0.5 text-xs mono text-[#FF8A00]" />
                <span className="text-zinc-600">%</span>
              </label>
              <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-zinc-400">
                $1,000 – $2,999
                <input type="number" min="1" max="100" step="5" value={puntCaps.under3k} disabled={busy}
                       onChange={(e) => setPuntCap({ under3k: e.target.value })}
                       data-testid="punt-cap-under-3k"
                       className="w-16 bg-black border border-zinc-800 focus:border-[#FF8A00] outline-none px-2 py-0.5 text-xs mono text-[#FF8A00]" />
                <span className="text-zinc-600">%</span>
              </label>
              <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-zinc-400">
                $3,000 – $4,999
                <input type="number" min="1" max="100" step="5" value={puntCaps.under5k} disabled={busy}
                       onChange={(e) => setPuntCap({ under5k: e.target.value })}
                       data-testid="punt-cap-under-5k"
                       className="w-16 bg-black border border-zinc-800 focus:border-[#FF8A00] outline-none px-2 py-0.5 text-xs mono text-[#FF8A00]" />
                <span className="text-zinc-600">%</span>
              </label>
              <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-zinc-400"
                     title="How many sub-$1,000 bodies one lineup may carry. 6 = no limit. Locked cheap players always fit.">
                Max sub-$1k per lineup
                <input type="number" min="0" max="6" step="1" value={puntCaps.maxCheap} disabled={busy}
                       onChange={(e) => setPuntCap({ maxCheap: e.target.value })}
                       data-testid="punt-max-cheap-bodies"
                       className="w-14 bg-black border border-zinc-800 focus:border-[#FF8A00] outline-none px-2 py-0.5 text-xs mono text-[#FF8A00]" />
              </label>
              <button type="button" disabled={busy} data-testid="punt-cap-reset"
                      onClick={() => setPuntCap({ under1k: 25, under3k: 35, under5k: 40, maxCheap: 1 })}
                      className="mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 underline underline-offset-2">
                reset 25 / 35 / 40 · 1
              </button>
            </div>
          </div>
        )}
        </div>
        )}

        <div className="grid grid-cols-1 gap-3 mt-4">
          {excludedGamesCount > 0 && !busy && (
            <div
              data-testid="excluded-games-chip"
              className="col-span-full flex items-center justify-between gap-2 border border-amber-600/30 bg-amber-950/20 px-3 py-2 text-[11px]"
            >
              <div className="text-amber-400 mono uppercase tracking-widest">
                <span className="font-bold">{excludedGamesCount}</span>{" "}
                game{excludedGamesCount === 1 ? "" : "s"} locked out — those players will be filtered before the ILP runs
              </div>
              <button
                type="button"
                data-testid="clear-excluded-games"
                onClick={() => {
                  try { localStorage.setItem("wp_excluded_games", "[]"); } catch { /* private mode */ }
                  try { window.dispatchEvent(new CustomEvent("wp:excluded-games")); } catch { /* noop */ }
                }}
                className="text-amber-400 hover:text-amber-200 underline underline-offset-2 mono uppercase tracking-widest"
              >
                Clear
              </button>
            </div>
          )}
          {autoSenseBusy && (
            <div
              data-testid="autosense-running-banner"
              className="col-span-full flex items-center gap-2 border border-[#FFCC00]/40 bg-[#1a1405] px-3 py-2 text-[11px]"
            >
              <span className="mono uppercase tracking-widest text-[#FFCC00] font-bold">Auto-Sense is building</span>
              <span className="text-zinc-400">
                one tuning per tournament — watch it in the DK entries panel. Smart generation is paused until it finishes.
              </span>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button
              onClick={run}
              disabled={busy || autoSenseBusy || !poolReady || cashSubTypeMissing || showdownMismatch || !!flexIssue}
              title={autoSenseBusy ? "An Auto-Sense build is running" : (flexIssue || undefined)}
              data-testid="sunday-auto-run"
              className={`bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-50 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm py-3 transition-colors flex items-center justify-center gap-2 ${
                busy ? "flex-1" : "w-full"
              }`}
              style={busy ? {} : { boxShadow: "0 0 24px rgba(57,255,20,0.5)" }}
            >
              <Lightning size={16} weight="fill" />
              {busy ? "Generating…" : "Generate My Portfolio"}
            </button>            {busy && (
              // Stop button — cooperative cancel. Fires POST /api/optimize/stop
              // which flips a flag the running task polls between lineups.
              // Any lineups already built survive: they still run Monte Carlo
              // + Cluster Rotation and land in the Lineups tab. Prevents the
              // "15-minute stuck build with no exit" panic that scares users
              // away right before lock. Disabled once already-requested so
              // rapid double-taps don't spam the endpoint.
              <button
                type="button"
                onClick={stop}
                disabled={stopping}
                data-testid="sunday-auto-stop"
                title={stopRequested
                  ? "Stop already requested. Click again to FORCE-STOP (hard-abort — partial lineups still saved)."
                  : "Stop optimizing at the next lineup boundary. Every lineup built so far will still be saved to your portfolio."}
                className={`shrink-0 border-2 font-black uppercase tracking-widest text-xs py-3 px-4 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed ${
                  stopRequested
                    ? "border-[#FFCC00] bg-[#FFCC00] hover:bg-[#e0b400] text-black animate-pulse"
                    : "border-[#FF3B30] bg-black hover:bg-[#FF3B30] text-[#FF3B30] hover:text-black"
                }`}
              >
                <Prohibit size={14} weight="fill" />
                {stopping ? "…" : (stopRequested ? "Force Stop" : "Stop")}
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 border-t border-zinc-800 pt-3 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
          {busy ? (
            <div className="w-full" data-testid="optimize-progress-panel">
              <div className="flex items-center gap-2 mb-2">
                <Rocket size={14} weight="fill" className="text-[#39FF14] flex-shrink-0 animate-pulse" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold">
                    Building your <span className="text-[#39FF14]" data-testid="optimize-building-count">{progress?.target_lineups || numLineups}-lineup</span> portfolio
                    {progress?.total > Number(progress?.target_lineups || numLineups) && progress?.phase !== "simulating" && (
                      <span className="text-zinc-500 font-normal text-xs" data-testid="optimize-overbuild-note">
                        {" "}· {progress.total} rule-legal candidates → sim picks the best {progress?.target_lineups || numLineups}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 truncate" data-testid="optimize-progress-phase">
                    {progress?.phase_label || "Queued · warming up rule engine"}
                  </div>
                </div>
                <div className="mono text-[11px] text-[#39FF14] font-bold flex-shrink-0 tabular-nums" data-testid="optimize-progress-count">
                  {(() => {
                    // Sprint 60.63 — during `solving` show LINEUP-COUNT %
                    // (matches the N/M fraction users are actually watching).
                    // The 3-72% weighted-pipeline scale confused users into
                    // thinking the optimizer had regressed when 80/150 built
                    // read "40%" — technically the correct % of the *total*
                    // pipeline (ILP + Monte Carlo + Cluster Rotation) but
                    // semantically nonsense next to "80/150". In non-solving
                    // phases the pipeline % is still shown (there's no
                    // per-lineup fraction to display).
                    const cur = Number(progress?.current || 0);
                    const tot = Number(progress?.total || numLineups);
                    const pipelinePct = Math.min(100, Math.round(uiPct));
                    if (progress?.phase === "solving") {
                      const lineupPct = tot > 0
                        ? Math.min(100, Math.round((cur / tot) * 100))
                        : 0;
                      return `${cur}/${tot} · ${lineupPct}%`;
                    }
                    return `${pipelinePct}%`;
                  })()}
                </div>
                {progress?.elapsed != null && (
                  <div className="mono text-[10px] text-zinc-500 flex-shrink-0 tabular-nums">
                    {progress.elapsed.toFixed(1)}s
                  </div>
                )}
              </div>
              <div
                className="relative h-2 bg-zinc-900 border border-zinc-800 overflow-hidden"
                role="progressbar"
                data-testid="optimize-progress-bar"
                aria-valuenow={Math.min(100, Math.round(uiPct))}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-[#39FF14] transition-[width] duration-500 ease-out"
                  style={{
                    width: `${Math.min(100, uiPct)}%`,
                    boxShadow: "0 0 12px rgba(57,255,20,0.8)",
                  }}
                />
                {/* Indeterminate shimmer overlay for the "preparing" / "queued" phase
                    before per-lineup progress starts ticking. */}
                {(progress?.phase === "queued" || progress?.phase === "preparing") && (
                  <div className="absolute inset-0 opacity-40 animate-pulse bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
                )}
              </div>
              <div className="mt-2 text-[10px] text-zinc-600 leading-relaxed">
                Solving · contest rules, correlated game scripts, shadow-field avoidance, dup cap, then Field-EV ranking.
                {" "}Don't refresh — the toast will confirm when it's done.
              </div>
            </div>
          ) : poolReady ? (
            <>
              <ShieldCheck size={12} weight="fill" className="text-[#39FF14] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                Slate loaded · <span className="text-zinc-300">{pool.length} projected players</span>.
                {" "}When you click Generate, a one-tap gate collects your two real DK Two-Touch
                choices (value sleeper + ceiling anchor). Every user's build ships with a
                unique fingerprint. If you've already made ≥2 touches in the Player Pool, we skip the gate.
              </div>
            </>
          ) : !poolChecked ? (
            <>
              <ShieldCheck size={12} weight="fill" className="text-zinc-500 flex-shrink-0 mt-0.5 animate-pulse" />
              <div className="flex-1 text-zinc-500">Checking for a loaded slate…</div>
            </>
          ) : (
            <>
              <WarningCircle size={12} weight="fill" className="text-[#FFCC00] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                Slate not loaded yet. Head to <span className="text-[#39FF14] font-bold">Slate Selection</span>{" "}
                and either sync this week's DK slate from the quick-pick chips (recommended) or
                upload a DKSalaries CSV manually. Once a pool is projected, come back and click
                Generate.
              </div>
            </>
          )}
        </div>

        {/* Sprint 60.68 — Pro Mode bridge card REMOVED. The proprietary
            ruleset is now fully opaque to users; Smart Optimizer handles
            everything based on contest_type + field_size + max_entries.
            TwoTouchGate is still enforced (compliance), but presented
            as a guided-adjustment prompt rather than exposing rule
            knobs — see the gate itself for the copy. */}
      </div>

      {gateOpen && (
        <TwoTouchGate
          pool={pool}
          existingOverrides={userOverrides}
          onClose={() => setGateOpen(false)}
          onConfirm={onGateConfirm}
        />
      )}

      <CoverageConfirmDialog
        open={coverageGateOpen}
        coverage={coverage}
        onCancel={() => setCoverageGateOpen(false)}
        onConfirm={() => { coverageAckRef.current = true; setCoverageGateOpen(false); setTimeout(run, 0); }}
      />

      {dtGateOpen && (
        <DualThreatGate
          qbs={pool.filter((p) => p.position === "QB")}
          onClose={onDtGateClose}
          onContinue={onDtGateContinue}
        />
      )}
    </section>
  );
}

/*
  Dual-Threat gate — hard-block modal that fires the first time a user
  clicks Generate on a slate where no QB has been DT-flagged. Rationale:
  the Combat Stack Matrix's `primary_only` (naked QB, no receiver)
  branch is DESIGNED for rushing-heavy QBs (Josh Allen / Lamar / Hurts /
  Fields tier). Pocket passers with no stack are a leak — they lose the
  correlation edge that makes DFS QBs profitable. If no DT is flagged
  the schedule silently downgrades those slots to P+S which changes the
  portfolio construction. Forcing a conscious weekly answer keeps the
  edge sharp. Users who want to skip can still hit "Continue anyway".
*/
function DualThreatGate({ qbs, onClose, onContinue }) {
  const topQbs = [...qbs]
    .sort((a, b) => (b.market_mean_proj || b.avg_ppg || 0) - (a.market_mean_proj || a.avg_ppg || 0))
    .slice(0, 8);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="dt-gate-modal"
    >
      <div
        className="bg-[#121212] border-2 border-[#FF8A00] max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-[#FF8A00] animate-pulse" />
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF8A00] font-bold">
            Weekly Gate · Dual-Threat QBs
          </div>
        </div>
        <div className="heading text-2xl font-black text-white mb-3 uppercase tracking-tighter">
          Which QBs are dual-threat this week?
        </div>
        <div className="text-sm text-zinc-300 leading-relaxed mb-4">
          The optimizer's <span className="text-[#39FF14] font-bold">stack engine</span>{" "}
          uses <span className="text-[#FF8A00] font-bold">primary_only</span> (naked-QB, no-receiver)
          plays for rushing-heavy QBs — Josh Allen, Lamar, Hurts, Fields tier — where the QB's
          own rushing floor replaces the receiver correlation. <span className="text-white font-bold">
          Pocket passers without a stack are a leak.</span>
        </div>
        <div className="text-sm text-zinc-400 leading-relaxed mb-4">
          Before we build your portfolio, please flag the QBs you consider dual-threat this week
          in the Player Pool below (orange <span className="text-[#FF8A00] font-bold">DT</span>{" "}
          icon next to each QB row). Top projected QBs on your slate:
        </div>
        <div className="bg-black/50 border border-zinc-800 p-3 mb-5 max-h-40 overflow-y-auto">
          {topQbs.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">No QBs projected — check your slate.</div>
          ) : (
            <ul className="text-xs text-zinc-300 space-y-1">
              {topQbs.map((q) => (
                <li key={q.dk_id} className="flex justify-between gap-2">
                  <span className="truncate">{q.name} · {q.team}</span>
                  <span className="mono text-zinc-500 shrink-0">
                    ${(q.salary / 1000).toFixed(1)}k · {(q.market_mean_proj || q.avg_ppg || 0).toFixed(1)} proj
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid="dt-gate-close"
            onClick={onClose}
            className="flex-1 border border-[#FF8A00] text-[#FF8A00] hover:bg-[#FF8A00] hover:text-black font-black uppercase tracking-widest text-xs py-3 transition-colors"
          >
            Flag My DTs First
          </button>
          <button
            type="button"
            data-testid="dt-gate-continue"
            onClick={onContinue}
            title="No dual-threat QBs on this slate — skip the primary_only stack option and build anyway"
            className="flex-1 border border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300 font-bold uppercase tracking-widest text-xs py-3 transition-colors"
          >
            No DTs This Week · Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function FeatureChip({ children }) {
  return (
    <div className="inline-flex items-center gap-1.5 border border-zinc-800 bg-black/40 px-2 py-1">
      <CheckCircle size={10} weight="fill" className="text-[#39FF14] flex-shrink-0" />
      <span className="text-zinc-300 leading-tight">{children}</span>
    </div>
  );
}
